// 계열별 전공/직업 데이터 (2022 개정 교육과정 선택과목 안내서 기반)

export interface MajorCourse {
  name: string;
  description: string;
}

export interface Major {
  name: string;
  description: string;
  relatedJobs: string[];
  coreSubjects: string[];
  recommendedSubjects: string[];
  careerSubjects: string[];
  courses: MajorCourse[];
}

export interface Field {
  name: string;
  icon: string;
  majors: Major[];
}

export interface Track {
  id: string;
  name: string;
  description: string;
  color: string;
  bgColor: string;
  fields: Field[];
}

export const careerTracks: Track[] = [
  {
    id: 'humanities',
    name: '인문계열',
    description: '언어, 문학, 역사, 철학 등 인간과 문화를 탐구합니다',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    fields: [
      {
        name: '언어·문학',
        icon: '📖',
        majors: [
          {
            name: '국어국문학과',
            description: '우리말과 우리 문학을 연구하여 민족 문화를 계승·발전시킵니다',
            relatedJobs: ['작가', '기자', '국어교사', '출판편집자', '방송작가', '카피라이터'],
            coreSubjects: ['화법과 언어', '독서와 작문', '문학'],
            recommendedSubjects: ['세계사', '사회와 문화'],
            careerSubjects: ['주제 탐구 독서', '문학과 영상', '인문학과 윤리'],
            courses: [
              { name: '국어학개론', description: '국어의 음운·문법·어휘 체계를 연구하는 기초 과목' },
              { name: '국문학개론', description: '한국 문학의 전반적 흐름과 갈래를 이해하는 입문 과목' },
              { name: '한국현대문학사', description: '근현대 한국 문학의 역사와 대표 작품을 살펴보는 과목' },
              { name: '고전시가론', description: '향가·속요·가사 등 고전 시가를 분석하고 감상하는 과목' },
              { name: '한국어문법론', description: '한국어의 문장 구조와 문법 원리를 체계적으로 연구하는 과목' },
              { name: '문예창작론', description: '소설·시·수필 등 창작 기법을 익히는 실습 과목' }
            ]
          },
          {
            name: '영어영문학과',
            description: '영어권 문화에 대한 이해와 영어 의사소통 능력을 갖춘 인재를 양성합니다',
            relatedJobs: ['번역가', '통역사', '영어교사', '외교관', '무역전문가', '승무원'],
            coreSubjects: ['영어I', '영어II', '영어 독해와 작문'],
            recommendedSubjects: ['화법과 언어', '세계시민과 지리'],
            careerSubjects: ['영미 문학 읽기', '심화 영어', '영어 발표와 토론'],
            courses: [
              { name: '영문학개론', description: '영문학의 주요 장르와 작품을 이해하는 입문 과목' },
              { name: '영어학개론', description: '영어의 음운·문법·의미 구조를 연구하는 기초 과목' },
              { name: '영미소설', description: '영미권 대표 소설 작품을 읽고 분석하는 과목' },
              { name: '영미시', description: '영미 시의 형식과 주제를 감상·분석하는 과목' },
              { name: '영어음성학', description: '영어 발음의 체계와 원리를 연구하는 과목' },
              { name: '영문법', description: '영어 문장 구조와 문법 규칙을 심화 학습하는 과목' }
            ]
          },
          {
            name: '통번역학과',
            description: '통번역 기술과 언어권별 문화를 결합한 국제 전문가를 양성합니다',
            relatedJobs: ['동시통역사', '번역가', '외교관', '대사관직원', '글로벌기업직원'],
            coreSubjects: ['영어I', '영어II'],
            recommendedSubjects: ['제2외국어', '세계시민과 지리'],
            careerSubjects: ['심화 영어 독해와 작문', '심화 제2외국어'],
            courses: [
              { name: '통역입문', description: '순차·동시통역의 기초 기법을 익히는 과목' },
              { name: '번역이론', description: '번역의 원리와 다양한 번역 전략을 연구하는 과목' },
              { name: '동시통역실습', description: '부스에서 실시간 통역을 실습하는 고급 과목' },
              { name: '영한번역연습', description: '영어 문장을 한국어로 정확히 옮기는 실습 과목' },
              { name: '문학번역', description: '문학 작품 번역의 특성과 기법을 배우는 과목' }
            ]
          }
        ]
      },
      {
        name: '인문과학',
        icon: '🏛️',
        majors: [
          {
            name: '사학과',
            description: '역사적 사건과 사회적 변화를 연구하여 현재와 미래를 이해합니다',
            relatedJobs: ['역사학자', '학예사', '큐레이터', '기록연구사', '문화재전문가', '박물관 관리자', '박물관 안내사'],
            coreSubjects: ['세계사', '한국사', '세계시민과 지리'],
            recommendedSubjects: ['화법과 언어', '사회와 문화'],
            careerSubjects: ['동아시아 역사 기행', '역사로 탐구하는 현대 세계'],
            courses: [
              { name: '한국사개론', description: '한국사의 전체 흐름을 이해하는 기초 과목' },
              { name: '동양사개론', description: '중국·일본 등 동양사의 흐름을 살펴보는 과목' },
              { name: '서양사개론', description: '서양 문명의 형성과 발전을 이해하는 과목' },
              { name: '역사철학', description: '역사 해석의 방법과 의미를 연구하는 과목' },
              { name: '고고학개론', description: '유적과 유물을 통해 과거를 연구하는 입문 과목' }
            ]
          },
          {
            name: '철학과',
            description: '인간 존재, 윤리, 논리적 사고를 통해 세상의 근본적 질문에 답합니다',
            relatedJobs: ['철학연구원', '윤리교사', '상담사', '언론인', '정책연구원'],
            coreSubjects: ['현대사회와 윤리', '사회와 문화'],
            recommendedSubjects: ['독서와 작문', '문학'],
            careerSubjects: ['인간과 철학', '윤리와 사상', '논리와 사고'],
            courses: [
              { name: '서양철학사', description: '고대부터 현대까지 서양 철학의 흐름을 배우는 과목' },
              { name: '동양철학사', description: '동양 사상의 전개와 핵심 개념을 이해하는 과목' },
              { name: '논리학', description: '올바른 추론과 논증의 원리를 연구하는 과목' },
              { name: '윤리학', description: '도덕적 판단과 가치의 문제를 탐구하는 과목' },
              { name: '인식론', description: '지식의 본질과 한계를 탐구하는 과목' }
            ]
          },
          {
            name: '문화콘텐츠학과',
            description: '영화, 게임, 영상 등 다양한 디지털 미디어 콘텐츠를 창작·기획합니다',
            relatedJobs: ['콘텐츠기획자', 'PD', '게임개발자', '영상제작자', '웹툰작가'],
            coreSubjects: ['화법과 언어', '문학'],
            recommendedSubjects: ['사회와 문화', '정보'],
            careerSubjects: ['문학과 영상', '매체 의사소통', '미디어 영어'],
            courses: [
              { name: '콘텐츠기획론', description: '콘텐츠 발상과 기획의 방법론을 배우는 과목' },
              { name: '스토리텔링', description: '이야기 구조와 서사 기법을 연구하는 과목' },
              { name: '미디어론', description: '미디어의 특성과 사회적 영향력을 분석하는 과목' },
              { name: '게임학개론', description: '게임의 기획·제작 원리를 이해하는 입문 과목' },
              { name: '영상제작실습', description: '촬영·편집 등 영상 제작 과정을 실습하는 과목' }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'social',
    name: '사회계열',
    description: '경영, 경제, 법학, 사회복지 등 사회현상을 분석하고 해결합니다',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    fields: [
      {
        name: '상경계열',
        icon: '💼',
        majors: [
          {
            name: '경영학과',
            description: '기업과 조직의 운영·관리·전략을 연구하며 경영 이론과 실무를 학습합니다',
            relatedJobs: ['경영컨설턴트', '마케터', '재무분석가', '창업가', 'CEO', '판매업 종사자'],
            coreSubjects: ['확률과 통계', '경제', '사회와 문화'],
            recommendedSubjects: ['대수', '영어II'],
            careerSubjects: ['경제 수학', '법과 사회', '인공지능 기초'],
            courses: [
              { name: '경영학원론', description: '경영의 기본 개념과 기업 활동을 이해하는 입문 과목' },
              { name: '마케팅', description: '시장 분석과 마케팅 전략 수립을 배우는 과목' },
              { name: '회계원리', description: '재무제표 작성과 회계의 기초 원리를 배우는 과목' },
              { name: '재무관리', description: '기업의 자금 조달과 투자 의사결정을 연구하는 과목' },
              { name: '인적자원관리', description: '인재 채용·평가·보상 등 인사 관리 전략을 배우는 과목' },
              { name: '경영전략', description: '기업의 경쟁 우위와 성장 전략을 연구하는 과목' }
            ]
          },
          {
            name: '경제학과',
            description: '경제 현상과 경제 활동을 분석하여 사회적·경제적 문제를 해결합니다',
            relatedJobs: ['경제학자', '금융분석가', '공무원', '연구원', '투자분석가'],
            coreSubjects: ['경제', '확률과 통계', '대수'],
            recommendedSubjects: ['미적분I', '영어II'],
            careerSubjects: ['경제 수학', '인공지능 수학', '데이터 과학'],
            courses: [
              { name: '미시경제학', description: '개별 경제주체의 의사결정과 시장 원리를 연구하는 과목' },
              { name: '거시경제학', description: '국민소득·물가·실업 등 경제 전체의 흐름을 분석하는 과목' },
              { name: '경제통계학', description: '경제 데이터를 통계적으로 분석하는 방법을 배우는 과목' },
              { name: '국제경제학', description: '무역과 국제 금융의 원리를 연구하는 과목' },
              { name: '계량경제학', description: '경제 이론을 데이터로 검증하는 기법을 배우는 과목' }
            ]
          }
        ]
      },
      {
        name: '사회과학계열',
        icon: '🌐',
        majors: [
          {
            name: '심리학과',
            description: '인간의 마음과 행동을 과학적으로 연구하여 삶의 질을 높입니다',
            relatedJobs: ['심리상담사', '임상심리사', '산업심리전문가', '연구원'],
            coreSubjects: ['사회와 문화', '현대사회와 윤리'],
            recommendedSubjects: ['확률과 통계', '생명과학'],
            careerSubjects: ['인간과 심리', '인간과 철학', '윤리문제 탐구'],
            courses: [
              { name: '심리학개론', description: '인간 마음과 행동의 기초 원리를 이해하는 입문 과목' },
              { name: '인지심리학', description: '기억·지각·사고 등 인지 과정을 연구하는 과목' },
              { name: '발달심리학', description: '인간의 생애주기별 심리적 발달을 연구하는 과목' },
              { name: '사회심리학', description: '사회적 상황 속 인간 행동을 분석하는 과목' },
              { name: '이상심리학', description: '정신장애의 원인과 증상을 이해하는 과목' },
              { name: '상담심리학', description: '상담의 이론과 실제 기법을 배우는 과목' }
            ]
          },
          {
            name: '정치외교학과',
            description: '정치 현상을 과학적으로 탐구하여 정치·외교 분야의 전문가를 양성합니다',
            relatedJobs: ['외교관', '정치분석가', 'NGO활동가', '국제기구직원', '정책연구원'],
            coreSubjects: ['정치', '법과 사회', '세계시민과 지리'],
            recommendedSubjects: ['세계사', '현대사회와 윤리'],
            careerSubjects: ['국제 관계의 이해', '경제', '윤리와 사상'],
            courses: [
              { name: '정치학개론', description: '정치 현상과 권력의 기본 원리를 이해하는 입문 과목' },
              { name: '국제정치론', description: '국가 간 관계와 국제 질서를 분석하는 과목' },
              { name: '비교정치론', description: '여러 국가의 정치체제를 비교 분석하는 과목' },
              { name: '외교사', description: '주요 외교 사건과 협상의 역사를 살펴보는 과목' },
              { name: '국제기구론', description: '유엔 등 국제기구의 역할과 기능을 연구하는 과목' }
            ]
          }
        ]
      },
      {
        name: '공공·안전',
        icon: '🚨',
        majors: [
          {
            name: '소방행정학과',
            description: '화재 예방·진압, 구조·구급 등 소방 행정 전반을 배워 소방공무원을 양성합니다',
            relatedJobs: ['소방공무원', '경찰공무원', '공무원'],
            coreSubjects: ['현대사회와 윤리', '사회와 문화', '생명과학'],
            recommendedSubjects: ['물리학', '체육1'],
            careerSubjects: ['법과 사회', '국제 관계의 이해'],
            courses: [
              { name: '소방학개론', description: '소방 행정의 기초 개념과 체계를 이해하는 과목' },
              { name: '소방법규', description: '소방 관련 법령과 규정을 학습하는 과목' },
              { name: '화재학', description: '화재의 원인과 연소 원리를 연구하는 과목' },
              { name: '구조구급론', description: '구조·구급 활동의 이론과 실무를 배우는 과목' },
              { name: '위험물질론', description: '위험물의 특성과 관리 방법을 배우는 과목' }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'science',
    name: '자연계열',
    description: '수학, 물리, 화학, 생명과학 등 자연의 원리를 탐구합니다',
    color: 'text-sky-700',
    bgColor: 'bg-sky-50',
    fields: [
      {
        name: '자연과학',
        icon: '🔬',
        majors: [
          {
            name: '물리학과',
            description: '자연 현상의 법칙을 연구하여 첨단 과학기술에 응용합니다',
            relatedJobs: ['물리학자', '반도체엔지니어', '항공우주공학자', '연구원'],
            coreSubjects: ['물리학', '대수', '미적분I'],
            recommendedSubjects: ['기하', '미적분II'],
            careerSubjects: ['역학과 에너지', '전자기와 양자', '행성우주과학'],
            courses: [
              { name: '일반역학', description: '물체의 운동 법칙과 힘을 연구하는 기초 과목' },
              { name: '전자기학', description: '전기와 자기 현상의 원리를 연구하는 과목' },
              { name: '양자역학', description: '미시 세계의 물리 법칙을 연구하는 고급 과목' },
              { name: '열역학', description: '열과 에너지의 관계를 연구하는 과목' },
              { name: '현대물리학', description: '상대성이론 등 현대 물리학의 성과를 배우는 과목' }
            ]
          },
          {
            name: '생명과학과',
            description: '생명체의 구성 원리와 생명 현상을 연구하는 학문입니다',
            relatedJobs: ['생명과학연구원', '의생명공학자', '바이오의약품연구원', '수의사'],
            coreSubjects: ['생명과학', '화학', '대수'],
            recommendedSubjects: ['미적분I', '물리학'],
            careerSubjects: ['세포와 물질대사', '생물의 유전', '융합과학 탐구'],
            courses: [
              { name: '일반생물학', description: '생명 현상의 기초 원리를 이해하는 입문 과목' },
              { name: '세포생물학', description: '세포의 구조와 기능을 연구하는 과목' },
              { name: '유전학', description: '유전의 원리와 유전자 기능을 연구하는 과목' },
              { name: '분자생물학', description: '생명 현상을 분자 수준에서 연구하는 과목' },
              { name: '생화학', description: '생명체 내 화학 반응을 연구하는 과목' }
            ]
          },
          {
            name: '수학과',
            description: '논리적 사고와 수학적 분석으로 복합적 문제를 해결하는 전문가를 양성합니다',
            relatedJobs: ['수학자', '통계학자', '데이터분석가', '암호학자', '금융공학자'],
            coreSubjects: ['대수', '미적분I', '확률과 통계'],
            recommendedSubjects: ['기하', '미적분II'],
            careerSubjects: ['인공지능 수학', '경제 수학', '수학과제 탐구'],
            courses: [
              { name: '해석학', description: '극한·연속·미적분의 엄밀한 이론을 연구하는 과목' },
              { name: '선형대수학', description: '벡터와 행렬, 선형 변환을 연구하는 과목' },
              { name: '미분방정식', description: '미분방정식의 해법과 응용을 배우는 과목' },
              { name: '정수론', description: '정수의 성질과 수론적 문제를 연구하는 과목' },
              { name: '위상수학', description: '공간의 연속적 성질을 연구하는 고급 과목' }
            ]
          }
        ]
      },
      {
        name: '의약학계열',
        icon: '🏥',
        majors: [
          {
            name: '의예과',
            description: '인간을 존중하며 질병을 진단·치료하는 의사를 양성합니다',
            relatedJobs: ['의사', '외과의', '내과의', '연구의', '의료관리자'],
            coreSubjects: ['생명과학', '화학', '물리학'],
            recommendedSubjects: ['미적분II', '확률과 통계'],
            careerSubjects: ['세포와 물질대사', '생물의 유전', '화학 반응의 세계'],
            courses: [
              { name: '해부학', description: '인체의 구조를 연구하는 기초의학 과목' },
              { name: '생리학', description: '인체 기관의 기능과 작용 원리를 연구하는 과목' },
              { name: '생화학', description: '생체 내 화학 반응과 대사를 연구하는 과목' },
              { name: '병리학', description: '질병의 원인과 발생 과정을 연구하는 과목' },
              { name: '약리학', description: '약물의 작용 원리를 연구하는 과목' }
            ]
          },
          {
            name: '약학과',
            description: '약물의 설계·제조·활용을 연구하여 건강 증진에 기여합니다',
            relatedJobs: ['약사', '제약연구원', '임상시험연구원', '약학교수'],
            coreSubjects: ['화학', '생명과학', '대수'],
            recommendedSubjects: ['물리학', '미적분I'],
            careerSubjects: ['물질과 에너지', '화학 반응의 세계', '세포와 물질대사'],
            courses: [
              { name: '약리학', description: '약물이 인체에 미치는 영향을 연구하는 과목' },
              { name: '약제학', description: '약의 제조와 제형 설계를 연구하는 과목' },
              { name: '생약학', description: '천연물 유래 약재를 연구하는 과목' },
              { name: '약품분석학', description: '의약품의 성분을 분석하는 기법을 배우는 과목' },
              { name: '임상약학', description: '환자 대상 약물 치료와 복약 지도를 배우는 과목' }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'engineering',
    name: '공학계열',
    description: '기계, 전자, 컴퓨터, 건축 등 기술로 세상을 바꾸는 분야입니다',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    fields: [
      {
        name: '정보·컴퓨터',
        icon: '💻',
        majors: [
          {
            name: '컴퓨터공학과',
            description: '컴퓨터 구조와 프로그래밍을 연구하여 첨단 IT 기술을 개발합니다',
            relatedJobs: ['소프트웨어개발자', 'AI엔지니어', '시스템엔지니어', '게임개발자'],
            coreSubjects: ['정보', '수학', '물리학'],
            recommendedSubjects: ['대수', '미적분I'],
            careerSubjects: ['인공지능 기초', '데이터 과학', '소프트웨어와 생활'],
            courses: [
              { name: '자료구조', description: '데이터를 효율적으로 저장·관리하는 구조를 배우는 과목' },
              { name: '알고리즘', description: '문제 해결을 위한 효율적인 알고리즘 설계를 배우는 과목' },
              { name: '운영체제', description: '컴퓨터 시스템 자원 관리의 원리를 배우는 과목' },
              { name: '컴퓨터구조', description: '컴퓨터 하드웨어의 동작 원리를 이해하는 과목' },
              { name: '데이터베이스', description: '데이터 모델링과 DB 설계·관리를 배우는 과목' }
            ]
          },
          {
            name: '인공지능학과',
            description: '머신러닝·딥러닝 기술을 활용한 AI 시스템을 개발합니다',
            relatedJobs: ['AI연구원', '데이터과학자', '머신러닝엔지니어', 'AI프로덕트매니저'],
            coreSubjects: ['정보', '확률과 통계', '대수'],
            recommendedSubjects: ['미적분I', '기하'],
            careerSubjects: ['인공지능 기초', '인공지능 수학', '데이터 과학'],
            courses: [
              { name: '머신러닝', description: '데이터로부터 학습하는 기계학습 알고리즘을 배우는 과목' },
              { name: '딥러닝', description: '신경망 기반의 심층 학습 기술을 연구하는 과목' },
              { name: '자연어처리', description: '컴퓨터가 언어를 이해하도록 하는 기술을 배우는 과목' },
              { name: '컴퓨터비전', description: '이미지·영상 인식 기술을 연구하는 과목' },
              { name: '강화학습', description: '보상 기반 학습 알고리즘을 배우는 고급 과목' }
            ]
          }
        ]
      },
      {
        name: '기계·전기·전자',
        icon: '⚙️',
        majors: [
          {
            name: '기계공학과',
            description: '각종 기계를 설계·제작하고 로봇, 자동차, 항공기 등 첨단 기술을 개발합니다',
            relatedJobs: ['기계공학자', '자동차공학자', '로봇공학자', '항공우주공학자', '항공기 기장', '자동차정비원'],
            coreSubjects: ['물리학', '대수', '미적분I'],
            recommendedSubjects: ['기하', '화학'],
            careerSubjects: ['역학과 에너지', '창의 공학 설계', '로봇과 공학세계'],
            courses: [
              { name: '정역학', description: '정지한 물체에 작용하는 힘을 분석하는 기초 과목' },
              { name: '동역학', description: '운동하는 물체의 힘과 운동을 연구하는 과목' },
              { name: '열역학', description: '열과 에너지 변환의 원리를 연구하는 과목' },
              { name: '유체역학', description: '유체의 흐름과 특성을 연구하는 과목' },
              { name: '재료역학', description: '재료의 변형과 응력을 분석하는 과목' }
            ]
          },
          {
            name: '전자공학과',
            description: '전자 이론을 바탕으로 반도체, AI, 로봇 등 첨단 전자 기술을 개발합니다',
            relatedJobs: ['전자공학자', '반도체엔지니어', '통신공학자', 'IoT전문가', '전기기사'],
            coreSubjects: ['물리학', '수학', '정보'],
            recommendedSubjects: ['화학', '미적분II'],
            careerSubjects: ['전자기와 양자', '역학과 에너지', '소프트웨어와 생활'],
            courses: [
              { name: '회로이론', description: '전기 회로의 동작 원리와 해석을 배우는 과목' },
              { name: '전자기학', description: '전기·자기 현상의 원리를 연구하는 과목' },
              { name: '디지털공학', description: '디지털 논리 회로 설계를 배우는 과목' },
              { name: '반도체공학', description: '반도체 소자의 구조와 동작 원리를 연구하는 과목' },
              { name: '신호및시스템', description: '신호 처리와 시스템 분석 기법을 배우는 과목' }
            ]
          }
        ]
      },
      {
        name: '교통·환경·에너지',
        icon: '🚄',
        majors: [
          {
            name: '철도운수학과',
            description: '철도 운영·운송·안전 관리를 배워 철도 전문 인력을 양성합니다',
            relatedJobs: ['철도기관사', '한국교통공사 직원', '버스기사', '택시기사'],
            coreSubjects: ['물리학', '대수', '영어I'],
            recommendedSubjects: ['기하', '정보'],
            careerSubjects: ['역학과 에너지', '창의 공학 설계'],
            courses: [
              { name: '철도운송론', description: '철도 운송의 체계와 운영 원리를 배우는 과목' },
              { name: '철도차량공학', description: '철도 차량의 구조와 원리를 연구하는 과목' },
              { name: '철도신호제어', description: '철도 신호와 제어 시스템을 배우는 과목' },
              { name: '교통안전론', description: '교통사고 예방과 안전 관리를 연구하는 과목' },
              { name: '물류관리론', description: '물류와 운송 관리의 원리를 배우는 과목' }
            ]
          },
          {
            name: '항공운항학과',
            description: '항공기 운항과 객실 서비스, 항공 안전을 배워 항공 전문 인력을 양성합니다',
            relatedJobs: ['항공기 기장', '승무원'],
            coreSubjects: ['물리학', '영어I', '영어II'],
            recommendedSubjects: ['대수', '기하'],
            careerSubjects: ['역학과 에너지', '영어 발표와 토론'],
            courses: [
              { name: '항공운항론', description: '항공기 운항의 원리와 절차를 배우는 과목' },
              { name: '항공기상학', description: '항공 운항에 필요한 기상 지식을 배우는 과목' },
              { name: '항공안전관리', description: '항공 안전과 위험 관리를 연구하는 과목' },
              { name: '객실서비스론', description: '기내 서비스와 승객 응대를 배우는 과목' },
              { name: '항공법규', description: '항공 관련 법령과 규정을 학습하는 과목' }
            ]
          },
          {
            name: '환경공학과',
            description: '환경 오염 방지와 자원·에너지 관리를 연구하여 환경 문제를 해결합니다',
            relatedJobs: ['환경관리공단 직원', '가스공사 직원', '환경미화원'],
            coreSubjects: ['화학', '생명과학', '대수'],
            recommendedSubjects: ['물리학', '지구과학'],
            careerSubjects: ['기후변화와 환경생태', '물질과 에너지', '융합과학 탐구'],
            courses: [
              { name: '환경공학개론', description: '환경공학의 기초 개념과 분야를 이해하는 과목' },
              { name: '수질관리', description: '수질 오염 방지와 처리 기술을 배우는 과목' },
              { name: '대기오염관리', description: '대기오염 방지 기술을 배우는 과목' },
              { name: '폐기물처리', description: '폐기물의 처리와 자원화를 연구하는 과목' },
              { name: '신재생에너지', description: '태양광·풍력 등 신재생에너지를 배우는 과목' }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'arts',
    name: '예체능계열',
    description: '미술, 음악, 체육, 디자인 등 예술적 창의력을 발휘하는 분야입니다',
    color: 'text-rose-700',
    bgColor: 'bg-rose-50',
    fields: [
      {
        name: '미술·디자인',
        icon: '🎨',
        majors: [
          {
            name: '시각디자인학과',
            description: '디지털 환경에서 창의적이고 실용적인 디자인을 개발합니다',
            relatedJobs: ['그래픽디자이너', 'UX디자이너', '영상디자이너', '브랜드디자이너', '미술관 관리자'],
            coreSubjects: ['미술', '사회와 문화'],
            recommendedSubjects: ['정보', '세계사'],
            careerSubjects: ['미술 창작', '미술과 매체', '디지털 기기 활용'],
            courses: [
              { name: '타이포그래피', description: '글자의 조형과 배열을 디자인하는 과목' },
              { name: '일러스트레이션', description: '이미지 표현 기법과 일러스트 제작을 배우는 과목' },
              { name: '그래픽디자인', description: '인쇄·디지털 매체의 시각 디자인을 실습하는 과목' },
              { name: '브랜드디자인', description: '브랜드 아이덴티티 설계를 배우는 과목' },
              { name: 'UX/UI디자인', description: '사용자 경험과 인터페이스 디자인을 연구하는 과목' }
            ]
          },
          {
            name: '미술교육과',
            description: '회화, 조소, 디자인 등 다양한 미술 실기와 교육학을 학습합니다',
            relatedJobs: ['미술교사', '미술치료사', '문화예술교육사', '큐레이터', '미술관 관리자', '박물관 안내사'],
            coreSubjects: ['미술', '교양'],
            recommendedSubjects: ['화법과 언어', '현대사회와 윤리'],
            careerSubjects: ['미술 창작', '미술 감상과 비평', '교육의 이해'],
            courses: [
              { name: '미술교육론', description: '미술 교육의 이론과 방법을 연구하는 과목' },
              { name: '회화', description: '회화의 기초와 표현 기법을 실습하는 과목' },
              { name: '조소', description: '입체 조형의 기법을 실습하는 과목' },
              { name: '미술사', description: '미술의 역사적 흐름을 이해하는 과목' },
              { name: '미술감상교육', description: '미술 작품 감상과 비평 교육 방법을 배우는 과목' }
            ]
          }
        ]
      },
      {
        name: '체육계열',
        icon: '⚽',
        majors: [
          {
            name: '체육교육과',
            description: '스포츠와 운동을 통해 신체적·정신적 발달의 효과를 교육 현장에 적용합니다',
            relatedJobs: ['체육교사', '스포츠코치', '운동처방사', '스포츠행정가', '운동선수', '운동선수 매니저', '물리치료사'],
            coreSubjects: ['체육1', '체육2', '생명과학'],
            recommendedSubjects: ['사회와 문화', '현대사회와 윤리'],
            careerSubjects: ['스포츠 문화', '스포츠 과학', '운동과 건강'],
            courses: [
              { name: '체육교육론', description: '체육 교육의 이론과 지도 방법을 연구하는 과목' },
              { name: '운동생리학', description: '운동이 신체에 미치는 생리적 영향을 연구하는 과목' },
              { name: '스포츠심리학', description: '운동 수행과 심리적 요인의 관계를 연구하는 과목' },
              { name: '운동역학', description: '운동 동작의 역학적 원리를 분석하는 과목' },
              { name: '체육측정평가', description: '체력 측정과 평가 방법을 배우는 과목' }
            ]
          }
        ]
      },
      {
        name: '조리·보건',
        icon: '🍳',
        majors: [
          {
            name: '조리·제과제빵학과',
            description: '한식·양식·제과제빵 등 전문 조리 기술과 외식 경영을 배웁니다',
            relatedJobs: ['요리사', '제빵사', '바리스타'],
            coreSubjects: ['기술·가정', '생명과학', '사회와 문화'],
            recommendedSubjects: ['화학', '미술'],
            careerSubjects: ['창의 공학 설계', '미술 창작'],
            courses: [
              { name: '조리기초실습', description: '기본 조리 기술과 위생을 실습하는 과목' },
              { name: '제과제빵실습', description: '빵·과자 제조 기술을 실습하는 과목' },
              { name: '식품위생학', description: '식품의 안전과 위생 관리를 배우는 과목' },
              { name: '외식경영론', description: '외식 사업의 운영과 경영을 배우는 과목' },
              { name: '커피학개론', description: '커피의 원산지와 추출 원리를 배우는 과목' }
            ]
          },
          {
            name: '물리치료학과',
            description: '운동·물리적 치료를 통해 환자의 신체 기능 회복을 돕는 전문가를 양성합니다',
            relatedJobs: ['물리치료사', '운동처방사'],
            coreSubjects: ['생명과학', '물리학', '대수'],
            recommendedSubjects: ['화학', '체육1'],
            careerSubjects: ['세포와 물질대사', '스포츠 과학', '운동과 건강'],
            courses: [
              { name: '해부학', description: '인체의 근골격계 구조를 연구하는 과목' },
              { name: '운동치료학', description: '운동을 통한 치료 방법을 배우는 과목' },
              { name: '신경계물리치료', description: '신경계 질환의 재활을 연구하는 과목' },
              { name: '정형물리치료', description: '근골격계 질환의 치료를 배우는 과목' },
              { name: '전기치료학', description: '전기 자극을 이용한 치료를 배우는 과목' }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'education',
    name: '교육계열',
    description: '미래 세대를 이끌 교육 전문가를 양성하는 분야입니다',
    color: 'text-teal-700',
    bgColor: 'bg-teal-50',
    fields: [
      {
        name: '교육',
        icon: '🎓',
        majors: [
          {
            name: '교육학과',
            description: '교육의 이론과 실제를 연구하고 효과적인 교육 방법을 개발합니다',
            relatedJobs: ['교육학연구원', '교육컨설턴트', '교육정책전문가', '교육과정개발자'],
            coreSubjects: ['화법과 언어', '사회와 문화', '현대사회와 윤리'],
            recommendedSubjects: ['확률과 통계', '인간과 심리'],
            careerSubjects: ['교육의 이해', '인간과 심리', '인간과 철학'],
            courses: [
              { name: '교육학개론', description: '교육의 본질과 기본 개념을 이해하는 입문 과목' },
              { name: '교육심리학', description: '학습과 발달의 심리적 원리를 연구하는 과목' },
              { name: '교육철학', description: '교육의 목적과 가치를 철학적으로 탐구하는 과목' },
              { name: '교육과정', description: '교육과정의 설계와 운영을 연구하는 과목' },
              { name: '교육사회학', description: '교육과 사회의 관계를 분석하는 과목' }
            ]
          },
          {
            name: '초등교육과',
            description: '아동 발달에 대한 이해를 바탕으로 유능한 초등교사를 양성합니다',
            relatedJobs: ['초등학교 교사', '방과후교사', '교육연구원', '교육행정가'],
            coreSubjects: ['화법과 언어', '독서와 작문'],
            recommendedSubjects: ['현대사회와 윤리', '확률과 통계'],
            careerSubjects: ['교육의 이해', '인간과 심리', '아동발달과 부모'],
            courses: [
              { name: '초등교육론', description: '초등교육의 특성과 방법을 이해하는 과목' },
              { name: '아동발달', description: '아동의 신체·인지·정서 발달을 연구하는 과목' },
              { name: '국어교육론', description: '초등 국어 교과의 지도 방법을 배우는 과목' },
              { name: '수학교육론', description: '초등 수학 교과의 지도 방법을 배우는 과목' },
              { name: '통합교과교육', description: '초등 통합 교과의 운영을 배우는 과목' }
            ]
          }
        ]
      }
    ]
  }
];

// Holland 흥미 유형
export interface HollandType {
  id: string;
  name: string;
  description: string;
  traits: string[];
  recommendedTracks: string[];
}

export const hollandTypes: HollandType[] = [
  {
    id: 'R',
    name: '현실형',
    description: '손을 이용한 구체적인 작업과 기계, 도구, 동물 다루기를 좋아합니다',
    traits: ['손재주 있음', '기계 조작 잘함', '야외 활동 선호', '실용적 사고'],
    recommendedTracks: ['engineering', 'science']
  },
  {
    id: 'I',
    name: '탐구형',
    description: '지적 호기심이 강하고 분석적·논리적 사고로 문제를 해결하길 좋아합니다',
    traits: ['분석적 사고', '지적 호기심', '독립적 작업', '논리적 추론'],
    recommendedTracks: ['science', 'engineering']
  },
  {
    id: 'A',
    name: '예술형',
    description: '창의적 활동과 자유로운 표현을 즐기며 예술적 감각이 뛰어납니다',
    traits: ['창의성', '표현력', '심미감', '독창적 아이디어'],
    recommendedTracks: ['arts', 'humanities']
  },
  {
    id: 'S',
    name: '사회형',
    description: '사람들과 어울리고 도와주는 것을 좋아하며 뛰어난 공감 능력을 가집니다',
    traits: ['공감 능력', '소통 능력', '봉사 정신', '협력적'],
    recommendedTracks: ['education', 'social']
  },
  {
    id: 'E',
    name: '진취형',
    description: '리더십이 강하고 목표 달성을 위한 설득과 경쟁을 즐깁니다',
    traits: ['리더십', '설득력', '목표 지향성', '도전 정신'],
    recommendedTracks: ['social', 'humanities']
  },
  {
    id: 'C',
    name: '관습형',
    description: '정확하고 체계적인 작업을 선호하며 규칙과 절차를 잘 따릅니다',
    traits: ['꼼꼼함', '체계적 사고', '정확성', '계획성'],
    recommendedTracks: ['social', 'science']
  }
];

// 가치관 유형
export interface ValueType {
  id: string;
  name: string;
  description: string;
  recommendedTracks: string[];
}

export const valueTypes: ValueType[] = [
  { id: 'achievement', name: '성취감', description: '도전적인 목표를 달성하며 성취감을 느끼고 싶다', recommendedTracks: ['engineering', 'science'] },
  { id: 'helping', name: '사회공헌', description: '다른 사람을 돕고 사회에 기여하는 삶을 살고 싶다', recommendedTracks: ['education', 'social'] },
  { id: 'creativity', name: '창의·표현', description: '나만의 개성을 표현하고 창의적인 결과물을 만들고 싶다', recommendedTracks: ['arts', 'humanities'] },
  { id: 'intellectual', name: '지적 탐구', description: '새로운 지식을 탐구하고 깊이 연구하는 활동이 좋다', recommendedTracks: ['science', 'humanities'] },
  { id: 'leadership', name: '영향력', description: '조직을 이끌고 큰 영향력을 발휘하고 싶다', recommendedTracks: ['social', 'engineering'] },
  { id: 'stability', name: '안정·전문성', description: '전문 영역에서 체계적으로 성장하며 안정을 추구한다', recommendedTracks: ['education', 'social'] }
];